import React, { useCallback, useEffect, useRef, useState } from "react";
import { addDays, addMonths, format } from "date-fns";
import { ecMonth, ecProperty, ecQuote } from "@/lib/easyConcierge";

const ymd = (d) => format(d, "yyyy-MM-dd");

// Loads the EasyConcierge listing + nightly availability; active=false means fallback to the request form.
export function useEcCalendar(ecId) {
  const [info, setInfo] = useState(null);
  const [days, setDays] = useState({});
  const loaded = useRef(new Set());

  useEffect(() => {
    setDays({});
    loaded.current = new Set();
    if (!ecId) { setInfo(false); return; }
    ecProperty(ecId)
      .then((d) => setInfo(d.success ? (d.property || d) : false))
      .catch(() => setInfo(false));
  }, [ecId]);

  const loadMonth = useCallback((date) => {
    if (!info || !ecId) return;
    [0, 1].forEach((k) => {
      const m = addMonths(date, k);
      const key = `${m.getFullYear()}-${m.getMonth() + 1}`;
      if (loaded.current.has(key)) return;
      loaded.current.add(key);
      ecMonth(ecId, m.getFullYear(), m.getMonth() + 1).then((r) => {
        if (!r.success || !r.calendar) return;
        const add = {};
        Object.entries(r.calendar).forEach(([k2, v]) => { add[k2] = !!v.is_available; });
        setDays((prev) => ({ ...prev, ...add }));
      }).catch(() => loaded.current.delete(key));
    });
  }, [info, ecId]);

  useEffect(() => { if (info) { loadMonth(new Date()); } }, [info, loadMonth]);

  return { loading: info === null, active: !!info, info: info || null, days, loadMonth };
}

// Checkout can land on an unavailable night; only the nights in between must be free.
export const ecDisabledMatcher = (days, range) => (date) => {
  if (range?.from && !range?.to && date > range.from) {
    for (let d = new Date(range.from); d < date; d = addDays(d, 1)) {
      if (days[ymd(d)] === false) return true;
    }
    return false;
  }
  return days[ymd(date)] === false;
};

export function useEcQuote(ecId, active, range, guests) {
  const [state, setState] = useState({ quote: null, error: null, loading: false });
  const refresh = useCallback(() => {
    if (!active || !range?.from || !range?.to) { setState({ quote: null, error: null, loading: false }); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    ecQuote(ecId, ymd(range.from), ymd(range.to), guests)
      .then((d) => {
        if (d.success) setState({ quote: d, error: null, loading: false });
        else setState({ quote: null, error: (d.errors && d.errors.join(" · ")) || d.error || "Séjour indisponible", loading: false });
      })
      .catch(() => setState({ quote: null, error: "Tarif indisponible pour le moment", loading: false }));
  }, [ecId, active, range?.from, range?.to, guests]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [refresh]);
  return { ...state, refresh };
}

const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}€`;

const Row = ({ label, value, testid, strong }) => (
  <div className={`flex justify-between ${strong ? "font-semibold text-nj-text" : "text-nj-text2"}`} data-testid={testid}>
    <span>{label}</span><span>{value}</span>
  </div>
);

export function EcQuoteBox({ quote, error, loading }) {
  if (loading) return <div className="mt-4 text-sm text-nj-muted" data-testid="ec-quote-loading">Calcul du prix…</div>;
  if (error) return <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-3" data-testid="ec-quote-error">{error}</div>;
  if (!quote) return null;
  const b = quote.breakdown || {};
  const dep = quote.deposit || {};
  return (
    <div className="mt-4 space-y-1.5 text-sm" data-testid="ec-quote">
      <Row label={`${quote.nights} nuit${quote.nights > 1 ? "s" : ""}`} value={eur(b.subtotal)} testid="ec-quote-subtotal" />
      {quote.long_stay_discount?.amount ? <div className="text-xs text-nj-muted">dont remise long séjour −{eur(quote.long_stay_discount.amount)}</div> : null}
      {b.cleaning_fee ? <Row label="Ménage" value={eur(b.cleaning_fee)} /> : null}
      {b.linen_fee ? <Row label="Linge" value={eur(b.linen_fee)} /> : null}
      {b.options_total ? <Row label="Options" value={eur(b.options_total)} /> : null}
      {b.tourist_tax ? <Row label="Taxe de séjour" value={eur(b.tourist_tax)} /> : null}
      <div className="border-t border-nj-olive pt-2"><Row label="Total" value={eur(b.total)} testid="ec-quote-total" strong /></div>
      {dep.is_partial && (
        <div className="bg-nj-olive/30 border border-nj-olive rounded-md p-2.5 mt-2" data-testid="ec-quote-deposit">
          <Row label={`À régler maintenant (${dep.percent}%)`} value={eur(dep.amount)} strong />
          <div className="text-xs text-nj-muted mt-1">Solde de {eur(dep.balance)} réglé plus tard.</div>
        </div>
      )}
    </div>
  );
}
